import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2 } from "@/components/icons";
import { supabase } from "@/lib/supabase";
import { FormField } from "./FormField";

type Props = { hasRecoveryToken: boolean };

export function ResetForm({ hasRecoveryToken }: Props) {
  if (hasRecoveryToken) return <UpdatePasswordForm />;
  return <RequestResetForm />;
}

function RequestResetForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation("auth");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <Alert>
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <AlertDescription>
          <Trans
            i18nKey="auth:resetPassword.linkSent"
            values={{ email }}
            components={{ strong: <strong /> }}
          />
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <FormField
        label={t("resetPassword.email")}
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? t("resetPassword.sending") : t("resetPassword.submit")}
      </Button>
    </form>
  );
}

function UpdatePasswordForm() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation("auth");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("resetPassword.passwordsMismatch"));
      return;
    }
    setSubmitting(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSubmitted(true);
    setTimeout(() => navigate("/auth/signin"), 2000);
  }

  if (submitted) {
    return (
      <Alert>
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <AlertDescription>{t("resetPassword.updated")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <FormField
        label={t("resetPassword.newPassword")}
        type="password"
        autoComplete="new-password"
        required
        minLength={8}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <FormField
        label={t("resetPassword.confirmNew")}
        type="password"
        autoComplete="new-password"
        required
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? t("resetPassword.updating") : t("resetPassword.updateSubmit")}
      </Button>
    </form>
  );
}
